/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { renderHomePageHeader } from "../../assets/component/renderHomePageHeader.js";
import HomepageMenuZh from "../../assets/component/HomepageMenuZh/HomepageMenuZh.js";
import FaqSections from "../../assets/component/FaqSections/FaqSections.js";

const GROUPS = [
  {
    id: "start",
    title: "開始使用",
    note: "開立帳戶及正式上線。",
    items: [
      {
        q: "CheckPlus 是甚麼？",
        a: "CheckPlus 是專為非營利機構而設的香港付款及捐款平台。機構可獲得網上帳戶、付款專頁及自動收據功能，無需自行開發或維護系統，即可收取付款和捐款。",
      },
      {
        q: "哪些機構可以登記？",
        a: "在香港註冊的慈善機構，包括根據《稅務條例》第 88 條獲豁免繳稅的機構，以及需要收取付款或捐款的學校、教會、社團和社會企業。",
      },
      {
        q: "設定需要多長時間？",
        a: "大部分機構可於同日開立帳戶並發布首個付款或捐款專頁。機構及銀行資料的核實會與支付服務供應商同步進行，一般於數個工作日內完成。",
      },
      {
        q: "我們需要技術人員嗎？",
        a: "不需要。專頁、付款項目、捐款計劃及收據全部可在 CheckPlus 帳戶內設定，無需安裝軟件，亦無需撰寫程式碼。",
      },
      {
        q: "需要預備甚麼資料？",
        a: "機構註冊文件、接收款項的銀行帳戶、授權聯絡人，以及付款專頁上希望顯示的文字和圖片。",
      },
    ],
  },
  {
    id: "payments",
    title: "付款與費用",
    note: "付款方式、平台費用及款項結算。",
    items: [
      {
        q: "支援哪些付款方式？",
        a: "所有計劃均可透過 Stripe 接受 Visa、MasterCard、American Express 及銀聯。Apple Pay、Google Pay 及 PayMe for Business 適用於 Pro 計劃，亦可視帳戶情況啟用其他本地及內地付款方式。",
      },
      {
        q: "CheckPlus 收費如何？",
        a: (
          <span>
            {
              "已在香港註冊的慈善機構可免費使用 Free 計劃（HK$0）。Plus 計劃按年付款為每月 HK$459（按月付款為 HK$550），Pro 計劃按年付款為每月 HK$792（按月付款為 HK$950）。詳情請參閱"
            }
            <Link to="/ZhPricing">價格頁面</Link>
            {"。"}
          </span>
        ),
      },
      {
        q: "平台費用是多少？",
        a: "Free 計劃的平台費用為 2%，Plus 及 Pro 計劃為 1%。支付處理費用由支付網關另行收取。",
      },
      {
        q: "捐款人可以代付交易費用嗎？",
        a: "可以。所有計劃均可讓捐款人選擇代付交易費用，令機構收到完整的預期金額。",
      },
      {
        q: "款項何時到達機構銀行帳戶？",
        a: "款項由支付服務供應商按其標準結算週期直接存入機構的銀行帳戶。CheckPlus 不會代持款項。",
      },
      {
        q: "可以接受每月定期捐款嗎？",
        a: "可以。Pro 計劃支援每月定期交易，每次成功付款亦會自動發出收據。",
      },
    ],
  },
  {
    id: "receipts",
    title: "捐款與收據",
    note: "收據、報表及捐款人記錄。",
    items: [
      {
        q: "收據會自動發出嗎？",
        a: "會。所有計劃在交易成功後，均會自動生成並以電郵發送付款及捐款收據。",
      },
      {
        q: "收據可以使用機構品牌嗎？",
        a: "可以。收據會顯示機構名稱及資料，Plus 及 Pro 計劃更可進一步自訂付款專頁及收據版面。",
      },
      {
        q: "可以發出可扣稅捐款收據嗎？",
        a: "如機構根據第 88 條獲豁免繳稅，CheckPlus 可發出載有香港捐款人報稅所需資料的捐款收據。收據內容的準確性仍由機構負責。",
      },
      {
        q: "可以重新發出或更正收據嗎？",
        a: "可以。收據可重新發送給捐款人，亦可在帳戶內重新發出已更正的記錄，確保報表準確。",
      },
      {
        q: "會提供哪些報表？",
        a: "Plus 及 Pro 計劃可生成涵蓋交易、捐款人及捐款計劃的系統報表，並可匯出作財務及審計用途。",
      },
    ],
  },
  {
    id: "security",
    title: "保安與私隱",
    note: "我們如何處理機構及捐款人的資料。",
    items: [
      {
        q: "資料儲存在哪裏？",
        a: "資料儲存於 Amazon Web Services 的基礎設施，並在傳輸及儲存時加密。",
      },
      {
        q: "CheckPlus 會儲存信用卡號碼嗎？",
        a: "不會。信用卡資料由已取得 PCI-DSS Level 1 認證的 Stripe 收集及處理，完整卡號不會進入 CheckPlus 系統。",
      },
      {
        q: "機構的捐款人資料會被出售嗎？",
        a: (
          <span>
            {
              "不會。CheckPlus 承諾在未獲特定同意的情況下，絕不出售或分享用戶資料給任何第三方公司。詳情請參閱"
            }
            <Link to="/ZhPrivacy">我們的私隱承諾</Link>
            {"。"}
          </span>
        ),
      },
      {
        q: "團隊成員可以看到甚麼？",
        a: "現階段每間機構只有一個帳戶，所有登入的人看到的資料相同。團隊帳戶（每位成員獨立登入及權限）即將推出。",
      },
    ],
  },
  {
    id: "account",
    title: "帳戶與支援",
    note: "更改計劃、語言及獲取協助。",
    items: [
      {
        q: "之後可以更改計劃嗎？",
        a: "升級可隨時進行。降級會於現有計費週期結束時生效，因此如選用按年付款，須待年度結束後才會轉換。",
      },
      {
        q: "CheckPlus 有中文版本嗎？",
        a: "有。平台及對外的付款專頁均提供英文、繁體中文及簡體中文，多語言專頁包含於 Pro 計劃。",
      },
      {
        q: "如何獲得支援？",
        a: (
          <span>
            {"請於"}
            <Link to="/ZhContact">聯絡頁面</Link>
            {
              "與我們的香港團隊聯絡。我們通常會在 2 至 4 個工作日內回覆。"
            }
          </span>
        ),
      },
    ],
  },
];

class ZhFaq extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
    };
    this.showModal = this.showModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  showModal() {
    this.setState({
      showModal: true,
    });
  }

  closeModal() {
    this.setState({
      showModal: false,
    });
  }

  render() {
    return (
      <div className="Faq-view">
        <Helmet>
          <title>常見問題 - CheckPlus</title>
        </Helmet>
        {!!this.state.showModal && (
          <HomepageMenuZh closeModal={this.closeModal} />
        )}
        {renderHomePageHeader(this)}

        <FaqSections
          title="有甚麼可以幫到你？"
          sub="香港非營利機構最常問我們的問題——關於開立帳戶、付款方式、費用、收據，以及如何保護捐款人資料。"
          searchPlaceholder="搜尋常見問題……"
          emptyText="找不到符合的答案。請嘗試其他關鍵字，或在下方聯絡我們。"
          groups={GROUPS}
          cta={{
            title: "還有其他問題？",
            txt: "我們的香港團隊通常會在 2 至 4 個工作日內回覆。",
            primary: "聯絡我們",
            to: "/ZhContact",
          }}
        />

        <div className="Faq-footer">
          <div className="Faq-footerNav">
            <Link to="/ZhTeam">
              <div className="Faq-footerNavItem">創辦人的故事</div>
            </Link>

            <Link to="/ZhFaq">
              <div className="Faq-footerNavItem">常見問題</div>
            </Link>

            <Link to="/ZhContact">
              <div className="Faq-footerNavItem">聯絡我們</div>
            </Link>

            <Link to="/ZhPrivacyPolicy">
              <div className="Faq-footerNavItem">私隱政策</div>
            </Link>

            <Link to="/ZhTerms">
              <div className="Faq-footerNavItem">使用條款</div>
            </Link>
          </div>

          <div className="Faq-footer_text">
            香港九龍油塘四山街 11 號
            <br />
            華利工業大廈 5 樓 A 室
            <br />
            <p />
            Copyright © 2026 CheckPlus Ltd. 保留一切權利。
          </div>
        </div>
      </div>
    );
  }
}

export default ZhFaq;
